import { PublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWithRetry } from "../utils/api/APIHelper";
import { AuthHelper } from "../utils/api/AuthHelper";
import {
  AUTH_FAILURE_WINDOW_MS,
  IMapSas,
  mapSasApiUrl,
  MAX_CONSECUTIVE_AUTH_FAILURES,
  SAS_MIN_REFRESH_DELAY_MS,
  SAS_REFRESH_BUFFER_MS,
  SAS_RETRY_INTERVAL_MS,
} from "../utils/maps/MapConstants";
import { getAzureMapSasUrl } from "../utils/maps/MapHelperFunctions";

interface UseMapSasResult {
  sas: IMapSas | undefined;
  isLoading: boolean;
  error: string | undefined;
  /** Forces an immediate re-fetch of the signature, e.g. after a tile failure. */
  forceRefresh: () => void;
  /** True once tiles keep failing despite fresh sigs, so the caller should remount the tile layers. */
  needsReset: boolean;
  /** Call after handling `needsReset` to resume normal retries. */
  acknowledgeReset: () => void;
}

// Avoid hammering the SAS endpoint when a burst of tiles fail around the same time.
const MIN_FORCE_REFRESH_INTERVAL_MS = 5000;

const createSasError = (message: string, retryable: boolean) =>
  Object.assign(new Error(message), { retryable });

const parseMapSas = (value: unknown): IMapSas => {
  if (!value || typeof value !== "object") {
    throw createSasError("Invalid map signature response", true);
  }

  const { expires, sig } = value as Partial<IMapSas>;
  const expiresAt = typeof expires === "string" ? Date.parse(expires) : NaN;
  if (
    typeof sig !== "string" ||
    sig.trim() === "" ||
    !Number.isFinite(expiresAt) ||
    expiresAt - Date.now() <= SAS_MIN_REFRESH_DELAY_MS
  ) {
    throw createSasError("Invalid map signature response", true);
  }

  return { expires: expires as string, sig };
};

/**
 * Fetches (and keeps fresh) the short-lived `sig`/`expires` pair used to
 * authorize map tile requests, so tiles don't need a JWT/preflight per request.
 *
 * The signature is fetched once per airport, then proactively refreshed
 * `SAS_REFRESH_BUFFER_MS` before it expires. `forceRefresh` is exposed so a
 * failed tile request (401/403) can trigger an immediate refresh as a fallback.
 */
export const useMapSas = (airportCode: string): UseMapSasResult => {
  const msalInstance = useMsal().instance as PublicClientApplication;
  const [sasState, setSasState] = useState<{
    airportCode: string;
    value: IMapSas;
  }>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [needsReset, setNeedsReset] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const isMountedRef = useRef(true);
  const activeAirportCodeRef = useRef(airportCode);
  const latestRequestIdRef = useRef(0);
  const lastFetchStartedAtRef = useRef(0);
  const consecutiveAuthFailuresRef = useRef(0);
  const lastAuthFailureAtRef = useRef(0);

  activeAirportCodeRef.current = airportCode;

  const fetchSas = useCallback(
    (code: string): Promise<void> => {
      if (inFlightRef.current) {
        return inFlightRef.current;
      }

      latestRequestIdRef.current += 1;
      const requestId = latestRequestIdRef.current;
      const isCurrentRequest = () =>
        isMountedRef.current &&
        activeAirportCodeRef.current === code &&
        latestRequestIdRef.current === requestId;
      const run = (async () => {
        try {
          lastFetchStartedAtRef.current = Date.now();
          setIsLoading(true);

          const accessToken = await AuthHelper.getAccessToken(msalInstance);
          if (accessToken === "") {
            throw createSasError("Access token cannot be empty!", false);
          }

          if (!process.env.REACT_APP_APIM_KEY) {
            throw createSasError("API key is not defined!", false);
          }

          const headers = {
            "x-api-key": process.env.REACT_APP_APIM_KEY,
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json;charset=UTF-8",
          };

          const url = getAzureMapSasUrl(code, mapSasApiUrl);
          const resp = await fetchWithRetry(
            url,
            { mode: "cors", headers, cache: "no-store" },
            2,
            300
          );
          const data = parseMapSas(await resp.json());

          if (!isCurrentRequest()) {
            return;
          }

          setSasState({ airportCode: code, value: data });
          setError(undefined);

          if (timerRef.current) {
            clearTimeout(timerRef.current);
          }

          const msUntilExpiry =
            new Date(data.expires).getTime() -
            Date.now() -
            SAS_REFRESH_BUFFER_MS;

          timerRef.current = setTimeout(
            () => fetchSas(code),
            Math.max(msUntilExpiry, SAS_MIN_REFRESH_DELAY_MS)
          );
        } catch (e: any) {
          if (isCurrentRequest()) {
            setError(e?.message || "Failed to fetch map signature");

            // The tile layer only mounts once a sig exists
            if (timerRef.current) {
              clearTimeout(timerRef.current);
            }
            timerRef.current = null;
            if (e?.retryable !== false) {
              timerRef.current = setTimeout(
                () => fetchSas(code),
                SAS_RETRY_INTERVAL_MS
              );
            }
          }
        } finally {
          if (isCurrentRequest()) {
            setIsLoading(false);
            inFlightRef.current = null;
          }
        }
      })();

      inFlightRef.current = run;
      return run;
    },
    [msalInstance]
  );

  useEffect(() => {
    isMountedRef.current = true;

    if (airportCode) {
      fetchSas(airportCode);
    }

    return () => {
      isMountedRef.current = false;
      inFlightRef.current = null;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [airportCode, fetchSas]);

  const forceRefresh = useCallback(() => {
    if (
      inFlightRef.current ||
      Date.now() - lastFetchStartedAtRef.current < MIN_FORCE_REFRESH_INTERVAL_MS
    ) {
      return;
    }

    // A tile keeps failing even right after a successful sig fetch, remount the tile layers
    const now = Date.now();
    if (now - lastAuthFailureAtRef.current > AUTH_FAILURE_WINDOW_MS) {
      consecutiveAuthFailuresRef.current = 0;
    }
    lastAuthFailureAtRef.current = now;
    consecutiveAuthFailuresRef.current += 1;

    if (consecutiveAuthFailuresRef.current > MAX_CONSECUTIVE_AUTH_FAILURES) {
      setNeedsReset(true);
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (airportCode) {
      fetchSas(airportCode);
    }
  }, [airportCode, fetchSas]);

  const acknowledgeReset = useCallback(() => {
    consecutiveAuthFailuresRef.current = 0;
    lastAuthFailureAtRef.current = 0;
    setNeedsReset(false);
  }, []);

  return {
    sas: sasState?.airportCode === airportCode ? sasState.value : undefined,
    isLoading,
    error,
    forceRefresh,
    needsReset,
    acknowledgeReset,
  };
};
